import { HistoricalDataPoint, StockSearchResult } from "../types";
import { getApiBase } from "@/lib/apiBase";

/**
 * Find the closing price for a given date from historical data.
 * Returns the exact match, or the closest earlier date if no exact match.
 * Returns null if no suitable data point is found.
 */
export function findPriceForDate(
  history: HistoricalDataPoint[],
  dateStr: string // YYYY-MM-DD
): number | null {
  if (!history || history.length === 0 || !dateStr) return null;

  // Exact match first
  const exact = history.find(d => d.date === dateStr);
  if (exact) return exact.price;

  // Find closest earlier date (handles weekends/holidays)
  let closest: HistoricalDataPoint | null = null;
  for (const point of history) {
    if (point.date <= dateStr) {
      if (!closest || point.date > closest.date) {
        closest = point;
      }
    }
  }

  return closest ? closest.price : null;
}

export const fetchExchangeRate = async (): Promise<number> => {
  try {
    const response = await fetch(`${getApiBase()}/api/exchange-rate`);
    if (!response.ok) {
      throw new Error(`API returned status: ${response.status}`);
    }
    const data = await response.json();
    return data.rate || 1.35;
  } catch (error) {
    console.error('Error fetching exchange rate', error);
    // Fallback static rate
    return 1.35;
  }
};

export const searchStocks = async (query: string): Promise<StockSearchResult[]> => {
  if (!query || query.length < 1) return [];

  try {
    const response = await fetch(`${getApiBase()}/api/stocks/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error(`API returned status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error searching stocks', error);
    return [];
  }
};

/**
 * Fetch the closing price for a symbol on a specific date from Yahoo Finance.
 * Used as a fallback when the date falls outside the locally cached 10-year history.
 */
export const fetchHistoricalPrice = async (
  symbol: string,
  date: string // YYYY-MM-DD
): Promise<number | null> => {
  try {
    const response = await fetch(
      `${getApiBase()}/api/stocks/historical-price?symbol=${encodeURIComponent(symbol)}&date=${encodeURIComponent(date)}`
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.price ?? null;
  } catch (error) {
    console.error('Error fetching historical price', { symbol, date }, error);
    return null;
  }
};

export const fetchStockData = async (symbol: string, options?: { includeProfile?: boolean }): Promise<{
  currentPrice: number;
  name: string;
  exchange: string;
  currency: string;
  history: HistoricalDataPoint[];
  groundingUrls: string[];
  marketCap: number | null;
  nextEarningsDate: string | null;
  instrumentType: string | null;
  sector: string | null;
}> => {
  try {
    const params = options?.includeProfile ? '?includeProfile=true' : '';
    const response = await fetch(`${getApiBase()}/api/stocks/${encodeURIComponent(symbol)}${params}`);
    if (!response.ok) {
      throw new Error(`API returned status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching stock data', { symbol }, error);
    throw error;
  }
};